import Mark from "mark.js";
import type { RefObject } from "react";
import { useEffect, useRef } from "react";

type Query = string | string[];

interface UseMarkOptions extends Mark.MarkOptions {
	enabled?: boolean;
}

export function useMark<T extends HTMLElement = HTMLDivElement>(
	query: Query,
	options?: UseMarkOptions,
): RefObject<T> {
	const ref = useRef<T>(null);
	const optionsRef = useRef(options);
	optionsRef.current = options;

	const terms = Array.isArray(query) ? query : [query];
	const normalizedQuery = terms
		.map((term) => term.trim())
		.filter(Boolean)
		.join("\u0000");

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const { enabled = true, ...markOptions } = optionsRef.current ?? {};
		const instance = new Mark(element);

		instance.unmark({
			done: () => {
				if (!enabled || !normalizedQuery) return;
				instance.mark(normalizedQuery.split("\u0000"), {
					separateWordSearch: false,
					...markOptions,
				});
			},
		});

		return () => {
			instance.unmark();
		};
	}, [normalizedQuery]);

	return ref;
}
